import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Fade,
  FormControlLabel,
  IconButton,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'

// Report download options. Options 1-4 are grouped, option 5 is the
// existing (unchanged) download flow, shown below a divider.
export const DOWNLOAD_REPORT_OPTIONS = [
  { value: 'UNALLOCATED', label: 'Unallocated Transactions' },
  { value: 'LDC_APPLIED', label: 'LDC Applied' },
  { value: 'NO_TDS', label: 'No TDS Transactions' },
  { value: 'ALL_TAB', label: 'All Tab Data' },
]

export const EXISTING_DOWNLOAD_OPTION = {
  value: 'PROCESSED',
  label: 'Processed Transactions',
}

const radioSx = {
  color: 'var(--forbrand-secondary)',
  '&.Mui-checked': {
    color: 'var(--forbrand-color)',
  },
}

const optionRowSx = selected => ({
  borderRadius: '8px',
  m: 0,
  px: 1.5,
  py: 1,
  width: '100%',
  backgroundColor: selected ? '#FFE8D4' : 'transparent',
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontWeight: 400,
    color: 'var(--forbrand-primary)' ,
  },
})

export default function DownloadReportPopper({
  open,
  anchorEl,
  onClose,
  onDownload,
  placement = 'bottom-end',
  defaultValue = EXISTING_DOWNLOAD_OPTION.value,
}) {
  const [selectedValue, setSelectedValue] = useState(defaultValue)

  // Reset the selection to the default (Existing Download) every time the
  // popper opens, so a prior choice never persists across open/close cycles.
  useEffect(() => {
    if (open) {
      setSelectedValue(defaultValue)
    }
  }, [open, defaultValue])

  const handleChange = event => {
    setSelectedValue(event.target.value)
  }

  const handleDownload = () => {
    onDownload?.(selectedValue)
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      modifiers={[
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
      ]}
      sx={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <Paper
            elevation={0}
            sx={{
              mt: 1,
              width: '420px',
              maxWidth: '95vw',
              borderRadius: '12px',
              border: '1px solid var(--border-subtel)',
              overflow: 'hidden',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              bgcolor: '#FFFFFF',
            }}
          >
            <Box sx={{ p: 3 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'var(--forbrand-primary)',
                  }}
                >
                  Download Report
                </Typography>
              </Box>

              {/* Options */}
              <RadioGroup value={selectedValue} onChange={handleChange}>
                {DOWNLOAD_REPORT_OPTIONS.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio sx={radioSx} />}
                    label={option.label}
                    sx={optionRowSx(selectedValue === option.value)}
                  />
                ))}

                <Divider sx={{ my: 1.5 }} />

                <FormControlLabel
                  value={EXISTING_DOWNLOAD_OPTION.value}
                  control={<Radio sx={radioSx} />}
                  label={EXISTING_DOWNLOAD_OPTION.label}
                  sx={optionRowSx(
                    selectedValue === EXISTING_DOWNLOAD_OPTION.value
                  )}
                />
              </RadioGroup>

              {/* Footer actions */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    color: 'var(--forbrand-primary)',
                    borderColor: '#8E95A2',
                    fontWeight: 400,
                    textTransform: 'none',
                    fontSize: '14px',
                    padding: '8px 16px',
                    minWidth: '120px',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDownload}
                  variant="contained"
                  sx={{
                    backgroundColor: '#FFAA71',
                    color: 'var(--forbrand-brand-varient)',
                    fontWeight: 400,
                    textTransform: 'none',
                    fontSize: '14px',
                    padding: '8px 16px',
                    minWidth: '120px',
                    boxShadow: 'none',
                  }}
                >
                  Download
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}
    </Popper>
  )
}
